import { Component, Input } from '@angular/core';
import { EChartsOption } from 'echarts';
import * as echarts from 'echarts';
import { ScreenType } from 'src/app/enums/ScreenType';
import { IncidentStatusTimeline } from 'src/app/models/incident';

@Component({
  selector: 'app-incident-timeline-serial-graph',
  templateUrl: './incident-timeline-serial-graph.component.html',
  styleUrl: './incident-timeline-serial-graph.component.scss',
})
export class IncidentTimelineSerialGraphComponent {
  @Input() set chartHeight(val: any) {
    if (val) this.height = val + 'px';
  }
  @Input() set chartData(val: IncidentStatusTimeline[]) {
    if (val) {
      this.charData = this.getUpdatedData(val);
      if (this.isSet) {
        this.setChartData();
      }
    }
  }
  @Input() set screenSize(val: ScreenType) {
    if (val) {
      this.screen = val;
      this.isSet = true;
      this.setChartData();
    }
  }
  isSet = false;
  chartOptions: EChartsOption;
  height = '300px';
  screen: ScreenType;
  charData: TimelineEntry[];
  ScreenType = ScreenType;
  statusColors: any = {
    0: '#F8AE54', // Open - Orange
    1: '#63993D', // Closed - Green
    2: '#4394E5', // Verified - OrangeRed
    3: '#876FD4', // Snoozed - DodgerBlue
    4: '#A3A3A3', // InProgress - LimeGreen
    5: '#FCC737', // OnHold - Gold (Updated color)
    6: '#A7D477', // Approved - SteelBlue
    7: '#F26B0F', // Rejected - DarkBlue
    8: '#006400', //Completed
    99: '#FF0000', // Unknown - Red
  };
  // Calculate the chart width based on number of data points
  getChartWidth(): string {
    const totalXLabels = this.charData?.[0]?.timestamps?.length || 0;
    if (totalXLabels <= 5) {
      return '100%';
    }

    // Ensure each timestamp (x-axis label) has ~100px width
    const minWidth = Math.max(totalXLabels * 100, 800);
    return `${minWidth}px`;
  }
  setChartData() {
    if (this.charData && this.charData.length > 0) {
      const { dates, series } = this.processChartData(this.charData);
      this.chartOptions = {
        color: [
          '#204f84',
          '#E93578',
          ...this.generateRandomColors(this.charData.length - 2),
        ],
        tooltip: {
          position: 'top',
          trigger: 'axis',
          formatter: (params: any) => {
            let filteredParams;
            if (params.length == 1) {
              filteredParams = params;
            } else {
              filteredParams = params.filter(
                (param: any) => param.seriesName !== 'Global'
              );
            }
            const validParams = filteredParams.filter(
              (param: any) => param.data !== null && param.data !== undefined
            );
            const tooltipData = validParams.map((param: any) => {
              const additionalData = param.data?.additionalInfo || 'N/A'; // Handle missing additionalInfo
              const seriesData = this.getStatusData(
                param.value ?? 99,
                param.seriesName
              );
              return `
                    <b>${
                      validParams.length === 1
                        ? param.seriesName || 'Unknown Series' // dynamically add the value
                        : param.seriesName || 'Unknown Series'
                    }</b><br/>
                     DateTime: ${seriesData.date ?? 'N/A'}<br/>
                     Status: ${seriesData.status}<br/>
                     Additional Info: ${additionalData}
  `;
            });

            return tooltipData.join('<br/><br/>');
          },
        },
        legend: {
          data: this.charData.map((entry) => entry.assignee),
          top: 0,
          padding: [5, 10],
          textStyle: {
            fontSize: 12,
          },
          selected: this.charData.reduce((acc: any, entry, index) => {
            // Only select the series with index 1, unselect the rest
            acc[entry.assignee] = index === 0;
            return acc;
          }, {}),
        },
        grid: {
          left: '3%',
          right: '4%',
          top: '60px',
          bottom: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          data: dates,
          axisLabel: {
            formatter: (value: string) => {
              const date = new Date(value);
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              });
            },
            interval: 0,
            rotate: 30,
          },
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: 24,
          interval: 4,
          axisLabel: {
            formatter: '{value}h',
          },
        },
        series,
      };
    }
  }
  processChartData(data: TimelineEntry[]) {
    const allDatesTemp: Date[] = [];
    const allDatesTempString: string[] = [];
    const series: any[] = [];
    data.forEach((entry) => {
      entry.timestamps.forEach((timestamp) => {
        allDatesTemp.push(new Date(timestamp.date));
      });
    });
    allDatesTemp.sort((a, b) => a.getTime() - b.getTime());
    allDatesTemp.forEach((x) => {
      allDatesTempString.push(x.toISOString());
    });
    const uniqueAllDatesTempString = [...new Set(allDatesTempString)];
    let i = 1;
    data.forEach((entry) => {
      let tempData: any[] = [];
      tempData = uniqueAllDatesTempString.map((date) => {
        const timestamp = entry.timestamps.find(
          (t) => new Date(t.date).toISOString() === date
        );
        let data;
        if (timestamp) {
          data = {
            time: timestamp.time,
            date: timestamp.date,
          };
        }
        return timestamp ? data : null;
      });

      let newData = tempData.map((data, index) => {
        const res =
          entry.timestamps.find((x) => x.time == data?.time)?.status ?? 99;
        return res;
      });
      let newTempData = tempData.map((data) => {
        if (data) {
          const temp = new Date(data.date)
            .toLocaleString('en-US', { hour12: false })
            .split(' ')[1]
            .toString()
            .split(':');
          const formatedData = parseFloat(temp[0] + '.' + temp[1]);
          return formatedData;
        }
        return null;
      });
      let markPoints: any[] = [];
      markPoints = newTempData.map((time, index) => ({
        name: this.getStatusLabel(newData[index]),
        value: this.getStatusLabel(newData[index]),
        coord: [index, time], // Use index for x-axis and time for y-axis
        label: {
          color: '#000',
          position: 'top',
        },
        itemStyle: {
          color: this.statusColors[newData[index]],
          // borderColor: '#000',
        },
        emphasis: {
          disabled: true,
        },
        // Set color of the markpoint
      }));
      series.push({
        name: entry.assignee,
        type: 'line',
        data: newTempData,
        smooth: true,
        symbol: 'circle',
        connectNulls: true,
        symbolSize: 8,
        lineStyle: {
          width: 3,
        },
        emphasis: {
          focus: 'series',
          itemStyle: {
            borderWidth: 3,
          },
        },
        markPoint: {
          data: markPoints,
          animation: false,
        },
      });
    });

    return {
      dates: uniqueAllDatesTempString,
      series,
    };
  }
  getUpdatedData(val: IncidentStatusTimeline[]): TimelineEntry[] {
    const updatedData: any[] = val.map((x) => {
      const updatedItem = Object.assign({}, x); // Create a shallow copy of the object
      if (!updatedItem.transitionBy) {
        updatedItem.transitionBy = 'AumYe';
      }
      return updatedItem;
    });
    const transitionByArray = updatedData.map((item) => item.transitionBy);
    const distinctNames = [...new Set(transitionByArray)];

    // Convert the data into the desired format
    let timelineEntries: TimelineEntry[] = distinctNames.map((name) => {
      // Filter data for the specific assignee (transitionBy)
      const filteredData = updatedData.filter(
        (item) => item.transitionBy === name
      );

      // Map the filtered data to the timestamp format
      const timestamps = filteredData.map((item) => ({
        date: item.date, // Format the date as string
        time: item.time, // Convert time to string
        status: item.status, // Convert status to string
        index: item.index,
      }));
      // Return the TimelineEntry object
      return {
        assignee: name,
        timestamps: timestamps,
      };
    });
    // let commonTimeline = timelineEntries.find((x) => x.assignee === 'AumYe')
    //   .timestamps[0];
    timelineEntries.forEach((x) => {
      x.timestamps = [...x.timestamps];
    });
    let globalTimeline: {
      date: Date;
      time: number;
      status: number;
      index: number;
    }[] = [];
    globalTimeline = timelineEntries.reduce(
      (allTimestamps, entry) => {
        return [...allTimestamps, ...entry.timestamps];
      },
      [] as {
        date: Date;
        time: number;
        status: number;
        index: number;
      }[]
    );
    const globalData = {
      assignee: 'Global',
      timestamps: globalTimeline,
    };
    timelineEntries = [globalData, ...timelineEntries];
    return timelineEntries;
  }
  private getStatusLabel(index: number): string {
    const statusLabels: any = {
      0: 'Open', // Open - Orange
      1: 'Closed', // Closed - Green
      2: 'Verified', // Verified - OrangeRed
      3: 'Snoozed', // Snoozed - DodgerBlue
      4: 'In Progress', // InProgress - LimeGreen
      5: 'On Hold', // OnHold - LimeGreen
      6: 'Approved', // Approved - SteelBlue
      7: 'Rejected', // Rejected - DarkBlue
      8: 'Completed', // InProgress - LimeGreen
      99: 'Unknown',
    };

    // Safely return the status label or "Unknown"
    return `${statusLabels[index] ?? 'Unknown'}`;
  }
  private getStatusFromData(time: number, assignee: string): any {
    let assigneeData = this.charData.find(
      (x) => x.assignee.toLowerCase() === assignee.toLowerCase()
    );
    let timestamps = assigneeData?.timestamps.find((x) => {
      const temp = new Date(x.date)
        .toLocaleString('en-US', { hour12: false })
        .split(' ')[1]
        .toString()
        .split(':');
      const formatedData = parseFloat(temp[0] + '.' + temp[1]);
      return formatedData == time;
    });
    return timestamps;
  }
  private getStatusData(index: number, assignee: string): any {
    const timestamps: any = this.getStatusFromData(index, assignee);
    const status: string = this.getStatusLabel(timestamps.status);
    const date: any = new Date(timestamps.date).toLocaleString('en-GB', {
      hour12: false,
    });
    const time: number = timestamps.time;
    const data = {
      status: status,
      date: date,
      time: time,
    };
    return data;
  }
  private generateRandomColors(numColors: number): string[] {
    const colors = [];
    for (let i = 0; i < numColors; i++) {
      const color = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
      colors.push(color);
    }
    return colors;
  }
}
export interface TimelineEntry {
  assignee: string;
  timestamps: {
    date: Date;
    time: number;
    status: number;
    index: number;
  }[];
}
